import { useAuth } from '../context/AuthContext';

/**
 * Hook to check if the current user has a specific permission.
 * The permissions list is populated by GET /api/auth/me.
 *
 * Usage:
 *   const canManageUsers = usePermission('tenant.users.manage');
 *   if (!canManageUsers) return <Forbidden />;
 */
export function usePermission(permission) {
  const { user } = useAuth();
  const perms = user?.permissions || [];
  return perms.includes(permission);
}

/**
 * Check any of several permissions (returns true if user has at least one).
 */
export function useAnyPermission(...permissions) {
  const { user } = useAuth();
  const perms = user?.permissions || [];
  return permissions.some((p) => perms.includes(p));
}

/**
 * Direct role check, without going through permissions.
 * Useful for UI that differs per role even if permissions overlap.
 */
export function useRole() {
  const { user } = useAuth();
  return user?.role || null;
}

/**
 * Is the current user the SaaS operator (platform admin)?
 */
export function useIsPlatformAdmin() {
  return useRole() === 'platform_admin';
}

/**
 * Is the current user a tenant admin (or platform admin, which supersedes)?
 */
export function useIsTenantAdmin() {
  const role = useRole();
  return role === 'admin' || role === 'platform_admin';
}

/**
 * Render children only if the user has the required permission.
 * <Can permission="tenant.users.manage"><Button>...</Button></Can>
 */
export function Can({ permission, anyOf, children, fallback = null }) {
  const { user } = useAuth();
  const perms = user?.permissions || [];

  let allowed = false;
  if (permission) {
    allowed = perms.includes(permission);
  } else if (anyOf && anyOf.length) {
    allowed = anyOf.some((p) => perms.includes(p));
  }

  return allowed ? <>{children}</> : fallback;
}
